import { useEffect, useRef, useState } from "react";

import type { ChartTimeTick } from "./chartTimeTicks";

const minimumDayLabelSpacing = 86;

export function visibleChartDayTicks(ticks: ChartTimeTick[], containerWidth: number, left: number, right: number) {
  if (!Number.isFinite(containerWidth) || containerWidth <= 0) return ticks;
  const maximumLabels = Math.max(1, Math.floor(containerWidth * (right - left) / 1000 / minimumDayLabelSpacing));
  if (ticks.length <= maximumLabels) return ticks;
  if (maximumLabels === 1) return [ticks[Math.floor(ticks.length / 2)]];
  return Array.from({ length: maximumLabels }, (_, index) => ticks[Math.round(index * (ticks.length - 1) / (maximumLabels - 1))]);
}

export function ChartTimeGrid({ ticks, dayTicks = [], top, bottom, left = 0, right = 1000 }: { ticks: ChartTimeTick[]; dayTicks?: ChartTimeTick[]; top: number; bottom: number; left?: number; right?: number }) {
  return <g aria-hidden="true">{ticks.map((tick) => {
    const x = left + tick.position * (right - left);
    return <line key={tick.position} className="chart-time-grid" x1={x} y1={top} x2={x} y2={bottom} />;
  })}{dayTicks.map((tick) => {
    const x = left + tick.position * (right - left);
    return <line key={tick.value} className="chart-day-grid" x1={x} y1={top} x2={x} y2={bottom} />;
  })}</g>;
}

export function ChartTimeLabels({ ticks, dayTicks = [], format, formatDay, left = 0, right = 1000 }: { ticks: ChartTimeTick[]; dayTicks?: ChartTimeTick[]; format: (value: number) => string; formatDay?: (value: number) => string; left?: number; right?: number }) {
  const rangeRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  useEffect(() => {
    const element = rangeRef.current;
    if (!element) return undefined;
    const updateWidth = () => setContainerWidth(element.clientWidth);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const displayedDayTicks = visibleChartDayTicks(dayTicks, containerWidth, left, right);
  const hasDayLabels = Boolean(formatDay && displayedDayTicks.length);
  return <div ref={rangeRef} className={`dynamic-chart-range chart-time-labels${hasDayLabels ? " chart-time-labels-with-days" : ""}`} aria-hidden="true">{!hasDayLabels && ticks.map((tick, index) => (
    <span className={`chart-time-label${index === ticks.length - 1 ? " chart-time-label-last" : ""}`} key={tick.position} style={{ left: `${(left + tick.position * (right - left)) / 10}%` }}>{format(tick.value)}</span>
  ))}{formatDay && displayedDayTicks.map((tick) => (
    <span className="chart-day-label" key={tick.value} style={{ left: `${(left + tick.position * (right - left)) / 10}%` }}>{formatDay(tick.value)}</span>
  ))}</div>;
}
